import React from 'react';
import PropTypes from 'prop-types';
import './StyleSheet.css'; 

/**
 * A helper function to format database-style keys (e.g., "document_number")
 * into a more readable, title-cased format (e.g., "Document Number").
 */
const formatHeader = (key) => {
  if (!key) return '';
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * A presentational component designed specifically to render a bot response that
 * contains tabular data from the SQL agent. It displays an introductory text
 * followed by a fully styled and responsive HTML table.
 */
const SqlTableMessage = ({ message }) => {
  const { introText, tableData, sqlFileUrls } = message;

  // Fallback if data is invalid or empty
  if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
    return <p className="sql-intro-text">{introText || "No data to display."}</p>;
  }

  const allHeaders = Object.keys(tableData[0]);
  const headersToDisplay = allHeaders.filter(header => header !== 'file_url');

  return (
    <div className="sql-table-container">
      {introText && <p className="sql-intro-text">{introText}</p>}
      <div className="table-wrapper">
        <table className="sql-response-table">
          <thead>
            <tr>
              {headersToDisplay.map((header) => (
                <th key={header}>{formatHeader(header)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, rowIndex) => {
                  const fileUrl = sqlFileUrls ? sqlFileUrls[rowIndex] : null;
                            return (
              <tr key={rowIndex}>
                {headersToDisplay.map((header, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`} data-label={formatHeader(header)}>
                    {/* Conditionally render the document number as a link */}
                    {header === 'document_number' && fileUrl ? (
                      <a 
                        href={fileUrl}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="table-link"
                      >
                        {row[header]}
                      </a>
                    ) : (
                      // Fallback for all other cells
                      typeof row[header] === 'object' && row[header] !== null
                        ? JSON.stringify(row[header])
                        : String(row[header])
                    )}
                  </td>                 
                ))}
              </tr>
                            );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SqlTableMessage.propTypes = {
  message: PropTypes.shape({
    introText: PropTypes.string,
    tableData: PropTypes.arrayOf(PropTypes.object).isRequired,
    sqlFileUrls: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default SqlTableMessage;